import { Button, Dropdown, notification, Popconfirm, Flex } from 'antd';
import {
  DownOutlined,
  PlusOutlined,
  EditOutlined,
  WarningOutlined,
  ContainerOutlined
} from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useAuthContext } from '../../../contexts/AuthContext';
import { useErrorMessage } from '../../../utils/errorMessage';
import { ExplanatoryDocumentMenu } from './utils/ExplanatoryDocumentMenu';
import { editingLocked } from '../../../utils/editingLocked';
import { routes } from '../../../utils/constants/adminRoutes';
import { useOwnerContext } from '../../../contexts/OwnerContext/OwnerContext';

/**
 * ExtraPageHeader component displays action buttons and menus in the owner's page header.
 * Provides actions: send mail, access explanatory documents, create a supervision, edit, or archive the owner.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {Function} props.setIsEmailModalOpen - Function to open the email modal.
 * @param {Function} props.setTemplateType - Function to set the mail template type.
 * @param {Function} props.setDocumentType - Function to set the explanatory document type.
 * @param {Object} props.profileLock - Object representing the profile lock state.
 * @param {string} props.id - The owner's identifier.
 * @param {Function} props.deleteOwner - Function to delete/archive the owner.
 * @returns {JSX.Element} The rendered component.
 */
export const ExtraPageHeader = ({
  setIsEmailModalOpen,
  setTemplateType,
  setDocumentType,
  profileLock,
  id,
  deleteOwner
}) => {
  const { t } = useTranslation();
  const { dispatchAPI } = useAuthContext();
  const { navigate } = useNavigate();
  const { message } = useErrorMessage();
  const { owner } = useOwnerContext();

  return (
    <Flex align="center" wrap gap={8}>
      <Button
        type="primary"
        onClick={() => {
          setIsEmailModalOpen(true);
          setTemplateType('SEND_MAIL');
          setDocumentType(null);
        }}
      >
        {t('buttons.send_mail')}
      </Button>
      <Dropdown
        overlay={
          <ExplanatoryDocumentMenu
            owner={owner}
            setIsEmailModalOpen={setIsEmailModalOpen}
            setTemplateType={setTemplateType}
            setDocumentType={setDocumentType}
          />
        }
      >
        <Button type="primary">
          {t('owners.show.buttons.explanatory_document')}
          <DownOutlined />
        </Button>
      </Dropdown>
      <Link
        to={{
          pathname: `${routes.SUPERVISIONS}/create`,
          search: `?id=${id}`
        }}
      >
        <Button type="primary" className="btn-main">
          <PlusOutlined />
          {`${t('owners.buttons.create_supervision')} `}
        </Button>
      </Link>
      <Link
        to={{
          pathname: editingLocked(profileLock, id)
            ? ''
            : `${routes.OWNERS}/edit/${id}`
        }}
      >
        <Button
          type="primary"
          onClick={() =>
            editingLocked(profileLock, id) &&
            notification.warning({
              message: t('pensioners.profile_lock'),
              duration: false
            })
          }
        >
          {`${t('buttons.edit')} `}
          <EditOutlined />
        </Button>
      </Link>
      <Popconfirm
        title={t('datatable.column.action.archive.title')}
        okText={t('datatable.column.action.archive.ok')}
        okButtonProps={{ type: 'danger' }}
        cancelText={t('datatable.column.action.archive.cancel')}
        onConfirm={() => deleteOwner({ dispatchAPI, navigate, id, message })}
        icon={<WarningOutlined />}
      >
        <Button type="link" danger>
          {`${t('buttons.archive')} `}
          <ContainerOutlined />
        </Button>
      </Popconfirm>
    </Flex>
  );
};

ExtraPageHeader.propTypes = {
  owner: PropTypes.shape({}),
  setIsEmailModalOpen: PropTypes.func,
  setTemplateType: PropTypes.func,
  setDocumentType: PropTypes.func,
  profileLock: PropTypes.shape({}),
  id: PropTypes.string,
  deleteOwner: PropTypes.func
};

ExtraPageHeader.defaultProps = {
  owner: {},
  setIsEmailModalOpen: () => {},
  setTemplateType: () => {},
  setDocumentType: () => {},
  profileLock: {},
  id: '',
  deleteOwner: () => {}
};
